import { App, Checkbox } from "antd";
import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import AlertsActions from "../reducer";
import { extendedAccessRoles } from "../../../Utils/permission";

export const isAttentionAlert = (record) => record?.alert_status?.toLowerCase() === "attention";

const useBulkAction = ({
  currentPageData,
  updateAlerts,
  baRole,
  isAdmin
}) => {
  const dispatch = useDispatch();
  const { notification } = App.useApp();
  // Available to every business account: only the member's role decides. The old
  // per-BA allowlist (Settings.bulk_alerts_resolving_ba_ids) belonged to the Adyen
  // "resolve to Auto-refunded" pilot, which this flow replaces.
  const isBulkActionEnabled = extendedAccessRoles.includes(baRole) && !isAdmin;
  const [isBulkActionActive, setIsBulkActionActive] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [bulkStatusSubmitting, setBulkStatusSubmitting] = useState(false);
  const bulkStatusUpdateDisabled = selectedRowKeys.length === 0;
  const selectedCount = selectedRowKeys.length;

  const eligibleRowKeys = useMemo(
    () => [...new Set(currentPageData.filter(isAttentionAlert).map((r) => r.alert_id))],
    [currentPageData],
  );

  const isAllSelected = eligibleRowKeys.length > 0 && selectedRowKeys.length === eligibleRowKeys.length;
  const isSelectionIndeterminate = selectedRowKeys.length > 0 && !isAllSelected;

  const setAllSelected = (checked) => setSelectedRowKeys(checked ? eligibleRowKeys : []);

  const isRowSelected = (id) => selectedRowKeys.includes(id);

  const toggleRowSelection = (id) => {
    setSelectedRowKeys((prev) =>
      prev.includes(id) ? prev.filter((key) => key !== id) : [...prev, id],
    );
  };

  const rowSelection = useMemo(() => {
    if (!isBulkActionActive) return null
    return {
      selectedRowKeys,
      onChange: setSelectedRowKeys,

      columnTitle: (
        <Checkbox
          checked={isAllSelected}
          indeterminate={isSelectionIndeterminate}
          onChange={(e) => setAllSelected(e.target.checked)}
        />
      ),

      getCheckboxProps: (record) => ({
        disabled: !isAttentionAlert(record),
      }),
    };
  }, [selectedRowKeys, eligibleRowKeys, isBulkActionActive, isAllSelected, isSelectionIndeterminate]);

  const onBulkAction = () => setIsBulkActionActive(true);

  const cancelBulkAction = () => {
    setIsBulkActionActive(false);
    setSelectedRowKeys([]);
    setBulkStatusModalOpen(false);
  };

  const onBulkStatusUpdate = () => setBulkStatusModalOpen(true);
  const closeBulkStatusModal = () => {
    if (bulkStatusSubmitting) return;
    setBulkStatusModalOpen(false);
  };

  const handleBulkStatusChange = (alert_status) => {
    setBulkStatusSubmitting(true);

    dispatch(
      AlertsActions.bulkUpdateAlertStatusRequest({
        alert_ids: selectedRowKeys,
        alert_status,
        onSuccess: () => {
          notification.success({ message: "Alerts updated" });
          setBulkStatusSubmitting(false);
          updateAlerts && updateAlerts();
          cancelBulkAction();
        },
        onError: () => {
          notification.error({
            message: "Failed to update alerts status.",
          });
          setBulkStatusSubmitting(false);
        },
      }),
    );
  };

  return {
    isBulkActionEnabled,
    isBulkActionActive,
    rowSelection,
    selectedRowKeys,
    isRowSelected,
    toggleRowSelection,
    isAllSelected,
    isSelectionIndeterminate,
    setAllSelected,
    bulkStatusUpdateDisabled,
    selectedCount,
    bulkStatusModalOpen,
    bulkStatusSubmitting,
    onBulkAction,
    onBulkStatusUpdate,
    closeBulkStatusModal,
    handleBulkStatusChange,
    cancelBulkAction,
  };
};

export default useBulkAction
